import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
import { logger } from '../utils/logger';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

const AuthContext = createContext(null);

// Create namespaced logger for AuthContext
const log = logger.create('AuthContext');

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants for retry logic
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1500;

// Helper to clear all auth-related data
const clearAuthData = () => {
  localStorage.removeItem('session_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_timestamp');
};

// Helper to check if we're online
const isOnline = () => {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

// Helper for exponential backoff delay
const getRetryDelay = (attempt) => {
  return BASE_RETRY_DELAY * Math.pow(2, attempt);
};

// Helper to safely parse stored JSON
const safeParseJson = (jsonString, fallback = null) => {
  if (!jsonString) return fallback;
  try {
    return JSON.parse(jsonString);
  } catch {
    return fallback;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Refs for tracking state across async operations
  const isMountedRef = useRef(true);
  const isProcessingAuthRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe state update that checks if component is mounted
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  /**
   * Process session ID from URL hash (OAuth callback)
   */
  const processSessionId = useCallback(async (sessionId, retryCount = 0) => {
    // Prevent duplicate processing
    if (isProcessingAuthRef.current) {
      return;
    }
    isProcessingAuthRef.current = true;

    // Debug: Show the API URL being used (helps diagnose Android issues)
    log.debug('processSessionId - API URL:', API);
    log.debug('processSessionId - Session ID:', sessionId?.substring(0, 20) + '...');

    if (!isOnline()) {
      toast.error('No internet connection. Please check your network and try again.');
      safeSetState(setLoading, false);
      isProcessingAuthRef.current = false;
      return;
    }

    try {
      safeSetState(setLoading, true);
      
      const fetchUrl = `${API}/auth/fetch-session-data`;
      
      // Debug toast on native to show what URL we're hitting
      if (Capacitor.isNativePlatform()) {
        toast.info(`Connecting to server...`, { duration: 2000 });
      }
      
      log.debug('Fetching session data from:', fetchUrl);
      
      // Use native HTTP plugin on mobile to bypass WebView restrictions
      let response;
      let responseData;
      
      if (Capacitor.isNativePlatform()) {
        // On native, use CapacitorHttp (built into @capacitor/core v8)
        // This bypasses WebView restrictions entirely
        try {
          log.debug('Using CapacitorHttp for native request');
          const httpResponse = await CapacitorHttp.request({
            method: 'GET',
            url: fetchUrl,
            headers: { 
              'X-Session-ID': sessionId,
              'Content-Type': 'application/json'
            },
            connectTimeout: 20000,
            readTimeout: 20000
          });
          
          response = { status: httpResponse.status };
          responseData = httpResponse.data;
          log.debug('Native HTTP response status:', httpResponse.status);
        } catch (httpError) {
          log.error('Native HTTP error:', httpError.message || httpError);
          toast.error(`Network error: ${httpError.message || 'Connection failed'}`);
          throw httpError;
        }
      } else {
        // On web, use axios
        response = await axios.get(
          fetchUrl,
          { 
            headers: { 'X-Session-ID': sessionId },
            timeout: 20000,
            validateStatus: (status) => status >= 200 && status < 600
          }
        );
        responseData = response.data;
      }

      // Handle 5xx errors (server errors) with retry
      if (response.status >= 500) {
        log.error('Server error:', response.status);
        if (retryCount < MAX_RETRIES) {
          log.info(`Server error ${response.status}, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          isProcessingAuthRef.current = false;
          await new Promise(resolve => setTimeout(resolve, getRetryDelay(retryCount)));
          return processSessionId(sessionId, retryCount + 1);
        }
        log.error('All retries failed for server error');
        clearAuthData();
        safeSetState(setLoading, false);
        isProcessingAuthRef.current = false;
        window.location.href = '/?auth_error=server_unavailable';
        return;
      }

      if (response.status !== 200) {
        log.error('Session data fetch failed:', response.status, responseData);
        if (response.status >= 400 && response.status < 500) {
          clearAuthData();
        }
        if (response.status === 503 && retryCount < MAX_RETRIES) {
          log.info(`Service unavailable, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          isProcessingAuthRef.current = false;
          await new Promise(resolve => setTimeout(resolve, getRetryDelay(retryCount)));
          return processSessionId(sessionId, retryCount + 1);
        }
        const errorMsg = responseData?.detail || 'Unable to fetch session data. Please try again.';
        toast.error(`Authentication failed: ${errorMsg}`);
        safeSetState(setLoading, false);
        isProcessingAuthRef.current = false;
        window.location.href = '/';
        return;
      }

      const sessionData = responseData;
      
      // Create session in our backend with retry logic
      let sessionCreated = false;
      for (let attempt = 0; attempt <= MAX_RETRIES && !sessionCreated; attempt++) {
        try {
          await axios.post(`${API}/auth/session`, sessionData, {
            timeout: 15000,
            validateStatus: (status) => status >= 200 && status < 600
          });
          sessionCreated = true;
        } catch (sessionError) {
          log.error(`Session creation attempt ${attempt + 1} failed:`, sessionError);
          if (attempt < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, getRetryDelay(attempt)));
          }
        }
      }

      if (!sessionCreated) {
        log.warn('Failed to create session after retries, continuing with OAuth data');
      }

      // Store auth data
      localStorage.setItem('session_token', sessionData.session_token);
      localStorage.setItem('user', JSON.stringify(sessionData));
      localStorage.setItem('auth_timestamp', Date.now().toString());
      
      safeSetState(setSessionToken, sessionData.session_token);
      safeSetState(setUser, sessionData);
      safeSetState(setIsAuthenticated, true);
      safeSetState(setLoading, false);
      
      isProcessingAuthRef.current = false;
      
      // On native platforms, don't use window.location.href as it can cause issues
      // The React router will handle the redirect based on isAuthenticated state
      if (!Capacitor.isNativePlatform()) {
        window.location.href = '/dashboard';
      }
      // On native, the LandingPage useEffect will detect isAuthenticated and navigate
    } catch (error) {
      log.error('Session processing error:', error);
      
      const shouldRetry = retryCount < MAX_RETRIES && (
        error.code === 'ECONNABORTED' || 
        error.code === 'ERR_NETWORK' ||
        error.code === 'ERR_BAD_RESPONSE' ||
        !error.response ||
        (error.response && error.response.status >= 500)
      );
      
      if (shouldRetry) {
        log.info(`Error occurred, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        isProcessingAuthRef.current = false;
        await new Promise(resolve => setTimeout(resolve, getRetryDelay(retryCount)));
        return processSessionId(sessionId, retryCount + 1);
      }
      
      clearAuthData();
      
      let errorMessage = 'Authentication failed';
      if (error.response) {
        const errorDetail = error.response.data?.detail || error.response.statusText;
        if (errorDetail && errorDetail.trim()) {
          errorMessage = errorDetail;
        }
      } else if (error.request) {
        // Request was made but no response - show the URL for debugging
        errorMessage = `Unable to connect to: ${API}. Check network.`;
        log.error('Auth timeout/network error. API:', API, 'Error:', error.code, error.message);
      } else {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      safeSetState(setLoading, false);
      isProcessingAuthRef.current = false;
      
      // On native platforms, don't force window.location redirect
      if (!Capacitor.isNativePlatform()) {
        window.location.href = '/';
      }
    }
  }, [safeSetState]);

  /**
   * Process session token from URL hash (direct Google OAuth callback)
   * Simpler than processSessionId — the token is already a valid session token
   */
  const processSessionToken = useCallback(async (token) => {
    if (isProcessingAuthRef.current) return;
    isProcessingAuthRef.current = true;

    log.debug('processSessionToken - token:', token?.substring(0, 12) + '...');

    if (!isOnline()) {
      toast.error('No internet connection. Please check your network and try again.');
      safeSetState(setLoading, false);
      isProcessingAuthRef.current = false;
      return;
    }

    try {
      safeSetState(setLoading, true);

      // Store the token immediately
      localStorage.setItem('session_token', token);
      localStorage.setItem('auth_timestamp', Date.now().toString());
      safeSetState(setSessionToken, token);

      // Fetch user data from our backend
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
        validateStatus: (status) => status >= 200 && status < 600
      });

      if (response.status === 200) {
        localStorage.setItem('user', JSON.stringify(response.data));
        safeSetState(setUser, response.data);
        safeSetState(setIsAuthenticated, true);
        safeSetState(setLoading, false);
        isProcessingAuthRef.current = false;

        if (!Capacitor.isNativePlatform()) {
          window.location.href = '/dashboard';
        }
      } else {
        throw new Error(`Auth check failed with status ${response.status}`);
      }
    } catch (error) {
      log.error('Session token processing error:', error);
      clearAuthData();
      toast.error('Authentication failed. Please try again.');
      safeSetState(setLoading, false);
      isProcessingAuthRef.current = false;

      if (!Capacitor.isNativePlatform()) {
        window.location.href = '/';
      }
    }
  }, [safeSetState]);

  /**
   * Check if the current session is valid
   */
  const checkSession = useCallback(async (token, retryCount = 0) => {
    if (!token) {
      safeSetState(setLoading, false);
      return false;
    }

    if (!isOnline()) {
      // Offline - use cached user data if available
      const cachedUser = safeParseJson(localStorage.getItem('user'));
      if (cachedUser) {
        log.info('Offline - using cached user data');
        safeSetState(setUser, cachedUser);
        safeSetState(setIsAuthenticated, true);
        safeSetState(setLoading, false);
        return true;
      }
      safeSetState(setLoading, false);
      return false;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
        validateStatus: (status) => status >= 200 && status < 600
      });
      
      // Handle 5xx errors with retry
      if (response.status >= 500 && retryCount < MAX_RETRIES) {
        log.info(`Session check server error, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, getRetryDelay(retryCount)));
        return checkSession(token, retryCount + 1);
      }
      
      if (response.status === 200) {
        // Update cached user data
        localStorage.setItem('user', JSON.stringify(response.data));
        localStorage.setItem('auth_timestamp', Date.now().toString());
        
        safeSetState(setUser, response.data);
        safeSetState(setIsAuthenticated, true);
        return true;
      } else if (response.status === 401) {
        log.info('Session invalid (401), clearing auth data');
        clearAuthData();
        safeSetState(setSessionToken, null);
        safeSetState(setUser, null);
        safeSetState(setIsAuthenticated, false);
        return false;
      } else if (response.status >= 500) {
        // Server error after retries - use cached data if available
        const cachedUser = safeParseJson(localStorage.getItem('user'));
        if (cachedUser) {
          log.info('Server unavailable - using cached user data');
          safeSetState(setUser, cachedUser);
          safeSetState(setIsAuthenticated, true);
          return true;
        }
        log.warn('Server unavailable after retries, no cached data');
        safeSetState(setIsAuthenticated, false);
        return false;
      } else {
        log.info('Session invalid, clearing auth data');
        clearAuthData();
        safeSetState(setSessionToken, null);
        safeSetState(setUser, null);
        safeSetState(setIsAuthenticated, false);
        return false;
      }
    } catch (error) {
      log.error('Session validation failed:', error);
      
      // Retry on network errors (timeout, network failure, etc.)
      const isNetworkError = error.code === 'ERR_NETWORK' || 
                             error.code === 'ERR_BAD_RESPONSE' || 
                             error.code === 'ECONNABORTED' ||
                             error.message?.includes('timeout') ||
                             error.message?.includes('Network Error');
      
      if (retryCount < MAX_RETRIES && isNetworkError) {
        log.info(`Session check network error, retrying... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, getRetryDelay(retryCount)));
        return checkSession(token, retryCount + 1);
      }
      
      // After all retries exhausted or on network error, try to use cached data
      // This ensures users stay logged in even if the server is temporarily unavailable
      const cachedUser = safeParseJson(localStorage.getItem('user'));
      if (cachedUser) {
        log.info('Using cached user data after network errors');
        safeSetState(setUser, cachedUser);
        safeSetState(setIsAuthenticated, true);
        // Keep the session token so future requests can work when server comes back
        return true;
      }
      
      // No cached data available - user will need to re-authenticate
      // But don't clear auth data - preserve session token for when server comes back
      log.warn('No cached user data available, but preserving session token');
      safeSetState(setIsAuthenticated, false);
      return false;
    } finally {
      safeSetState(setLoading, false);
    }
  }, [safeSetState]);

  /**
   * Logout the user
   */
  const logout = useCallback(async () => {
    try {
      if (sessionToken && isOnline()) {
        await axios.post(
          `${API}/auth/logout`,
          {},
          { 
            headers: { Authorization: `Bearer ${sessionToken}` },
            timeout: 5000
          }
        );
      }
    } catch (error) {
      log.error('Logout error:', error);
    } finally {
      clearAuthData();
      safeSetState(setSessionToken, null);
      safeSetState(setUser, null);
      safeSetState(setIsAuthenticated, false);
    }
  }, [sessionToken, safeSetState]);

  /**
   * Get auth headers for API requests
   */
  const getAuthHeaders = useCallback(() => {
    if (sessionToken) {
      return { Authorization: `Bearer ${sessionToken}` };
    }
    return {};
  }, [sessionToken]);

  // Initialize auth state
  useEffect(() => {
    // Add a safety timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      log.warn('Auth initialization timeout - forcing loading to false');
      safeSetState(setLoading, false);
    }, 5000); // Reduced to 5 second timeout
    
    log.debug('Auth initialization started');
    
    // Handle deep links from Capacitor (for mobile OAuth callback)
    let appUrlListener = null;
    if (Capacitor.isNativePlatform()) {
      log.debug('Setting up Capacitor deep link listener');
      try {
        appUrlListener = App.addListener('appUrlOpen', async (event) => {
        log.debug('Deep link received:', event.url);
        
        try {
          const urlString = event.url;
          
          // Check for session_token first (new direct Google OAuth flow)
          if (urlString.includes('session_token=')) {
            const match = urlString.match(/session_token=([^&\s#]+)/);
            if (match) {
              const token = decodeURIComponent(match[1]);
              log.debug('Session token extracted from deep link');
              try { await Browser.close(); } catch (e) {}
              clearAuthData();
              await processSessionToken(token);
              return;
            }
          }
          
          // Fall back to session_id (legacy Emergent auth flow)
          if (urlString.includes('session_id=')) {
            const match = urlString.match(/session_id=([^&\s#]+)/);
            if (match) {
              const sessionId = decodeURIComponent(match[1]);
              log.debug('Session ID extracted (legacy):', sessionId.substring(0, 20) + '...');
              try { await Browser.close(); } catch (e) {}
              clearAuthData();
              await processSessionId(sessionId);
              return;
            }
          }
          
          log.debug('No session token or ID found in deep link URL');
          
          // Check for OAuth errors - parse URL properly to get actual error message
          if (urlString.includes('auth_error') || urlString.includes('error_description') || urlString.includes('error=')) {
            let errorMessage = 'Authentication failed';
            try {
              const searchStr = urlString.includes('?') ? urlString.split('?')[1]?.split('#')[0] : '';
              const deepParams = new URLSearchParams(searchStr);
              errorMessage = deepParams.get('error_description') || deepParams.get('error') || errorMessage;
            } catch (e) {
              log.error('Error parsing deep link params:', e);
            }
            log.error('OAuth error in deep link:', errorMessage);
            try { await Browser.close(); } catch (e) {}
            toast.error(errorMessage);
          }
        } catch (parseError) {
          log.error('Error parsing deep link URL:', parseError);
          // Fallback extraction
          const url = event.url;
          const tokenMatch = url.match(/session_token=([^&\s#]+)/);
          const idMatch = url.match(/session_id=([^&\s#]+)/);
          if (tokenMatch) {
            try { await Browser.close(); } catch (e) {}
            clearAuthData();
            await processSessionToken(decodeURIComponent(tokenMatch[1]));
          } else if (idMatch) {
            try { await Browser.close(); } catch (e) {}
            clearAuthData();
            await processSessionId(decodeURIComponent(idMatch[1]));
          }
        }
      });
      } catch (e) {
        log.error('Failed to set up Capacitor deep link listener:', e);
      }
    }
    
    // Check for OAuth error in URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get('auth_error');
    const errorDescription = urlParams.get('error_description') || urlParams.get('error');
    
    if (authError || errorDescription) {
      log.error('OAuth error in URL:', authError || errorDescription);
      // Clear the error from URL
      window.history.replaceState(null, '', window.location.pathname);
      
      // Show user-friendly error message
      if (errorDescription?.includes('Invalid state')) {
        toast.error('Session expired. Please try signing in again.');
      } else if (errorDescription) {
        toast.error(`Authentication failed: ${errorDescription}`);
      } else {
        toast.error('Authentication failed. Please try again.');
      }
      
      // Check if user has an existing valid session
      const existingToken = localStorage.getItem('session_token');
      if (existingToken) {
        safeSetState(setSessionToken, existingToken);
        checkSession(existingToken);
        clearTimeout(loadingTimeout);
        return;
      }
      
      log.debug('Setting loading to false due to auth error');
      safeSetState(setLoading, false);
      clearTimeout(loadingTimeout);
      return;
    }
    
    const hash = window.location.hash;
    
    // New flow: session_token from direct Google OAuth
    if (hash && hash.includes('session_token=')) {
      try {
        const token = hash.split('session_token=')[1]?.split('&')[0];
        if (token) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          clearAuthData();
          processSessionToken(decodeURIComponent(token));
          clearTimeout(loadingTimeout);
          return;
        }
      } catch (error) {
        log.error('Error parsing session_token from URL:', error);
        toast.error('Authentication failed: Invalid session data');
        safeSetState(setLoading, false);
        clearTimeout(loadingTimeout);
        return;
      }
    }
    
    // Legacy flow: session_id from Emergent auth
    if (hash && hash.includes('session_id=')) {
      try {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          const decodedSessionId = decodeURIComponent(sessionId);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          clearAuthData();
          processSessionId(decodedSessionId);
          clearTimeout(loadingTimeout);
          return;
        }
      } catch (error) {
        log.error('Error parsing session_id from URL:', error);
        toast.error('Authentication failed: Invalid session data in URL');
        safeSetState(setLoading, false);
        clearTimeout(loadingTimeout);
        return;
      }
    }

    const token = localStorage.getItem('session_token');
    if (token) {
      log.debug('Found session token, validating...');
      safeSetState(setSessionToken, token);
      
      // Immediately load cached user data for instant UI
      // This ensures users see their data even if server is slow/unavailable
      const cachedUser = safeParseJson(localStorage.getItem('user'));
      if (cachedUser) {
        safeSetState(setUser, cachedUser);
        safeSetState(setIsAuthenticated, true);
        log.debug('Loaded cached user data for instant UI');
      }
      
      // Then validate session in background (will update if different)
      checkSession(token);
      clearTimeout(loadingTimeout);
    } else {
      log.debug('No session token found, setting loading to false');
      safeSetState(setLoading, false);
      clearTimeout(loadingTimeout);
    }
    
    return () => {
      clearTimeout(loadingTimeout);
      // Cleanup Capacitor listener
      if (appUrlListener) {
        appUrlListener.remove();
      }
    };
  }, [processSessionId, processSessionToken, checkSession, safeSetState]);

  // Listen for online/offline events - recheck session only when coming back online
  useEffect(() => {
    const handleOnline = () => {
      log.debug('Back online');
      // Don't automatically recheck - let user actions trigger auth if needed
    };

    const handleOffline = () => {
      log.debug('Went offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const value = {
    user,
    sessionToken,
    loading,
    isAuthenticated,
    logout,
    getAuthHeaders,
    checkSession: () => checkSession(sessionToken, 0),
    processSessionId
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
